import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";

import {
  Briefcase,
  Camera,
  CheckCircle2,
  FileText,
  GraduationCap,
  Image as ImageIcon,
  Languages as LanguagesIcon,
  Loader2,
  Paperclip,
  Plus,
  Send,
  Trash2,
  Upload,
  User,
  UserRoundCheck,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { FlagIcon } from "@/components/site/FlagIcon";
import { ChipInput } from "@/components/site/ChipInput";
import {
  CheckboxField,
  ComboboxField,
  SelectField,
  PhoneField,
  TextField,
  TextareaField,
} from "@/components/site/fields";
import { PageModal } from "@/components/site/PageModal";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { countryOptions, normalizeCountryName } from "@/lib/countries";
import { isValidPhone } from "@/lib/phone";
import { goldConfetti } from "@/lib/confetti";
import { reportFormError } from "@/lib/form-feedback";

import { useLocale, type Locale } from "@/lib/i18n";
import {
  DRIVER_CLASSES,
  JOB_EXTENTS,
  JOB_EXTENT_LABEL,
  LANGUAGE_LEVELS,
  LANGUAGE_LEVEL_LABEL,
  LANGUAGE_OPTIONS,
  LICENSE_COUNTRIES,
  LICENSE_SUBJECTS,
  languageFlag,
  languageLabel,
} from "@/lib/jobs";
import { listOpenPositions, submitJobApplication } from "@/lib/jobs.functions";
import { clearJobDraft, loadJobDraft, saveJobDraft } from "@/lib/job-draft";
import { visitorFields } from "@/lib/analytics-client";

const MAX_FILES = 5;
const MAX_BYTES = 8 * 1024 * 1024;

const TEXT: Record<Locale, Record<string, string>> = {
  sv: {
    heading: "Skicka in din ansökan",
    intro:
      "Fyll i formuläret så landar din ansökan direkt hos oss. Fält märkta med * behöver fyllas i.",
    position: "Tjänst du söker",
    about: "Om dig",
    firstName: "Förnamn",
    lastName: "Efternamn",
    email: "E-post",
    phone: "Telefon",
    city: "Ort",
    country: "Land",
    start: "Tidigast tillgänglig",
    extent: "Omfattning",
    background: "Bakgrund",
    education: "Utbildning",
    educationHint: "Examen, lärosäte och år.",
    license: "Jag har svensk lärarlegitimation",
    licenseDetails: "Ämnen och årskurser i legitimationen",
    years: "Års yrkeserfarenhet",
    languages: "Språk du behärskar i arbetet",
    drivers: "Jag har körkort",
    letter: "Personligt brev",
    letterHint: "Berätta kort om dig, hur du arbetar och varför du söker hos oss.",
    attachments: "Bilagor",
    attachmentsHint: "CV, betyg, legitimation och intyg. PDF, Word eller bild, max 8 MB per fil.",
    dropzone: "Dra hit filer, klistra in eller klicka för att välja",
    portrait: "Porträttbild (frivilligt)",
    portraitHint: "Ladda upp en bild eller ta ett foto direkt med kameran.",
    uploadPhoto: "Ladda upp bild",
    takePhoto: "Ta foto",
    stopCamera: "Avbryt",
    capture: "Ta bilden",
    references: "Referenser (frivilligt)",
    refName: "Namn",
    refRelation: "Relation",
    refPhone: "Telefon",
    refEmail: "E-post",
    refNotes: "Övrigt om referensen",
    refNotesHint: "T.ex. när ni arbetade ihop och när referensen är lättast att nå.",
    addRef: "Lägg till referens",
    consentPrefix: "Jag godkänner att skolan sparar och behandlar mina uppgifter enligt ",
    consentLink: "integritetspolicyn",
    consentSuffix: " i syfte att hantera min ansökan.",
    submit: "Skicka ansökan",
    sending: "Skickar…",
    doneTitle: "Tack för din ansökan!",
    doneBody:
      "Vi har tagit emot din ansökan och hör av oss så snart vi har läst den. Behöver du komplettera något går det bra att mejla join@sweintschool.com.",
    remove: "Ta bort",
  },
  en: {
    heading: "Send your application",
    intro: "Fill in the form and your application reaches us directly. Fields marked * are required.",
    position: "Position",
    about: "About you",
    firstName: "First name",
    lastName: "Last name",
    email: "Email",
    phone: "Phone",
    city: "City",
    country: "Country",
    start: "Earliest start",
    extent: "Scope",
    background: "Background",
    education: "Education",
    educationHint: "Degree, institution and year.",
    license: "I hold a Swedish teaching certificate",
    licenseDetails: "Subjects and year groups in the certificate",
    years: "Years of experience",
    languages: "Working languages",
    drivers: "I have a driving licence",
    letter: "Cover letter",
    letterHint: "Tell us briefly about you, how you work and why you are applying.",
    attachments: "Attachments",
    attachmentsHint: "CV, diplomas and certificates. PDF, Word or image, max 8 MB per file.",
    dropzone: "Drag files here, paste, or click to choose",
    portrait: "Portrait photo (optional)",
    portraitHint: "Upload a photo or take one with your camera.",
    uploadPhoto: "Upload photo",
    takePhoto: "Take photo",
    stopCamera: "Cancel",
    capture: "Capture",
    references: "References (optional)",
    refName: "Name",
    refRelation: "Relation",
    refPhone: "Phone",
    refEmail: "Email",
    refNotes: "Notes about the reference",
    refNotesHint: "E.g. when you worked together and when they are easiest to reach.",
    addRef: "Add reference",
    consentPrefix: "I agree that the school stores and processes my details according to the ",
    consentLink: "privacy policy",
    consentSuffix: " in order to handle my application.",
    submit: "Send application",
    sending: "Sending…",
    doneTitle: "Thank you for your application!",
    doneBody:
      "We have received your application and will get back to you once we have read it. Email join@sweintschool.com if you need to add anything.",
    remove: "Remove",
  },
  es: {
    heading: "Envía tu candidatura",
    intro: "Rellena el formulario y tu candidatura llegará directamente a nosotros. Los campos con * son obligatorios.",
    position: "Puesto",
    about: "Sobre ti",
    firstName: "Nombre",
    lastName: "Apellidos",
    email: "Correo electrónico",
    phone: "Teléfono",
    city: "Ciudad",
    country: "País",
    start: "Disponibilidad",
    extent: "Jornada",
    background: "Formación y experiencia",
    education: "Formación",
    educationHint: "Titulación, centro y año.",
    license: "Tengo titulación docente sueca",
    licenseDetails: "Materias y cursos incluidos",
    years: "Años de experiencia",
    languages: "Idiomas de trabajo",
    drivers: "Tengo carné de conducir",
    letter: "Carta de presentación",
    letterHint: "Cuéntanos brevemente sobre ti y por qué te presentas.",
    attachments: "Documentos",
    attachmentsHint: "CV, títulos y certificados. PDF, Word o imagen, máx. 8 MB por archivo.",
    dropzone: "Arrastra archivos, pega o haz clic para elegir",
    portrait: "Foto de perfil (opcional)",
    portraitHint: "Sube una foto o hazla con la cámara.",
    uploadPhoto: "Subir foto",
    takePhoto: "Hacer foto",
    stopCamera: "Cancelar",
    capture: "Capturar",
    references: "Referencias (opcional)",
    refName: "Nombre",
    refRelation: "Relación",
    refPhone: "Teléfono",
    refEmail: "Correo",
    refNotes: "Notas sobre la referencia",
    refNotesHint: "Por ejemplo, cuándo trabajasteis juntos y cuándo es más fácil localizarla.",
    addRef: "Añadir referencia",
    consentPrefix: "Acepto que la escuela guarde y trate mis datos según la ",
    consentLink: "política de privacidad",
    consentSuffix: " para gestionar mi candidatura.",
    submit: "Enviar candidatura",
    sending: "Enviando…",
    doneTitle: "¡Gracias por tu candidatura!",
    doneBody:
      "Hemos recibido tu candidatura y te responderemos en cuanto la hayamos leído. Escríbenos a join@sweintschool.com si necesitas añadir algo.",
    remove: "Eliminar",
  },
};

const PRIVACY_PATH: Record<Locale, string> = {
  sv: "/integritetspolicy",
  en: "/en/privacy-policy",
  es: "/es/politica-de-privacidad",
};

type Attachment = { name: string; dataUrl: string; size: number; type: string };

type JobReference = { name: string; relation: string; phone: string; email: string; notes: string };

function emptyRef(): JobReference {
  return { name: "", relation: "", phone: "", email: "", notes: "" };
}

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function strArray(value: unknown, fallback: string[]): string[] {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : fallback;
}

function readFile(file: File): Promise<Attachment> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Filen kunde inte läsas."));
    reader.onload = () =>
      resolve({
        name: file.name,
        dataUrl: String(reader.result ?? ""),
        size: file.size,
        type: file.type,
      });
    reader.readAsDataURL(file);
  });
}

function formatSize(bytes: number): string {
  return bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;
}

/** Rubrik med ikon för varje steg i formuläret. */
function SectionHeading({
  icon: Icon,
  title,
  hint,
}: {
  icon: typeof User;
  title: string;
  hint?: string | undefined;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center text-primary">
        <Icon className="h-4.5 w-4.5" aria-hidden="true" />
      </span>
      <div className="min-w-0">
        <h3 className="text-base font-semibold sm:text-lg">{title}</h3>
        {hint ? <p className="mt-0.5 text-sm text-foreground/70">{hint}</p> : null}
      </div>
    </div>
  );
}

export function JobApplicationForm() {
  const locale = useLocale();
  const t = TEXT[locale];
  const submit = useServerFn(submitJobApplication);
  const startedAt = useRef(Date.now());

  const positionsQuery = useQuery({
    queryKey: ["job-positions"],
    queryFn: () => listOpenPositions(),
    staleTime: 5 * 60 * 1000,
  });

  const positionOptions = useMemo(
    () =>
      (positionsQuery.data?.positions ?? []).map((p) => ({
        value: p.slug,
        label: p.title[locale] || p.title.sv,
      })),
    [positionsQuery.data, locale],
  );

  const [positionSlug, setPositionSlug] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [country, setCountry] = useState("");
  const [earliestStart, setEarliestStart] = useState("");
  const [extent, setExtent] = useState("");
  const [education, setEducation] = useState("");
  const [hasLicense, setHasLicense] = useState(false);
  const [licenseDetails, setLicenseDetails] = useState("");
  const [licenseCountry, setLicenseCountry] = useState("");
  const [licenseSubjects, setLicenseSubjects] = useState<string[]>([]);
  const [driversClasses, setDriversClasses] = useState<string[]>([]);
  const [driversCountry, setDriversCountry] = useState("");
  const [languageLevels, setLanguageLevels] = useState<Record<string, string>>({});
  const [years, setYears] = useState("");
  const [languages, setLanguages] = useState<string[]>([]);
  const [pendingLanguage, setPendingLanguage] = useState<string | null>(null);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [drivers, setDrivers] = useState(false);
  const [coverLetter, setCoverLetter] = useState("");
  const [refs, setRefs] = useState<JobReference[]>([emptyRef()]);
  const [files, setFiles] = useState<Attachment[]>([]);
  const [portrait, setPortrait] = useState<Attachment | null>(null);
  const [consent, setConsent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [company, setCompany] = useState("");
  const [done, setDone] = useState(false);

  const fileInput = useRef<HTMLInputElement>(null);
  const portraitInput = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [camera, setCamera] = useState(false);

  useEffect(() => {
    if (!positionSlug && positionOptions.length > 0) {
      setPositionSlug(positionOptions[0]?.value ?? "");
    }
  }, [positionOptions, positionSlug]);

  useEffect(() => () => stopCamera(), []);

  // Återställ påbörjad ansökan så inget tappas när man byter sida.
  const restored = useRef(false);
  useEffect(() => {
    const draft = loadJobDraft();
    restored.current = true;
    if (!draft) return;
    const v = draft.values;
    if (str(v["positionSlug"])) setPositionSlug(str(v["positionSlug"]));
    setFirstName(str(v["firstName"]));
    setLastName(str(v["lastName"]));
    setEmail(str(v["email"]));
    setPhone(str(v["phone"]));
    setCity(str(v["city"]));
    setCountry(normalizeCountryName(str(v["country"])));
    setEarliestStart(str(v["earliestStart"]));
    setExtent(str(v["extent"]));
    setEducation(str(v["education"]));
    setHasLicense(v["hasLicense"] === true);
    setLicenseDetails(str(v["licenseDetails"]));
    setLicenseCountry(str(v["licenseCountry"]));
    setLicenseSubjects(strArray(v["licenseSubjects"], []));
    setDriversClasses(strArray(v["driversClasses"], []));
    setDriversCountry(str(v["driversCountry"]));
    setDrivers(v["drivers"] === true);
    setYears(str(v["years"]));
    setLanguages(strArray(v["languages"], []));
    if (v["languageLevels"] && typeof v["languageLevels"] === "object") {
      setLanguageLevels(v["languageLevels"] as Record<string, string>);
    }
    setCoverLetter(str(v["coverLetter"]));
    if (Array.isArray(v["refs"]) && v["refs"].length > 0) {
      setRefs(
        (v["refs"] as Record<string, unknown>[]).map((r) => ({
          name: str(r["name"]),
          relation: str(r["relation"]),
          phone: str(r["phone"]),
          email: str(r["email"]),
          notes: str(r["notes"]),
        })),
      );
    }
  }, []);

  // Spara löpande (bilagor och foto sparas inte – de kan vara för stora).
  useEffect(() => {
    if (!restored.current || done) return;
    const timer = window.setTimeout(() => {
      saveJobDraft({
        positionSlug,
        firstName,
        lastName,
        email,
        phone,
        city,
        country,
        earliestStart,
        extent,
        education,
        hasLicense,
        licenseDetails,
        licenseCountry,
        licenseSubjects,
        driversClasses,
        driversCountry,
        drivers,
        years,
        languages,
        languageLevels,
        coverLetter,
        refs,
      });
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    positionSlug,
    firstName,
    lastName,
    email,
    phone,
    city,
    country,
    earliestStart,
    extent,
    education,
    hasLicense,
    licenseDetails,
    licenseCountry,
    licenseSubjects,
    driversClasses,
    driversCountry,
    drivers,
    years,
    languages,
    languageLevels,
    coverLetter,
    refs,
    done,
  ]);

  function stopCamera() {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    setCamera(false);
  }

  async function startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      setCamera(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      }, 30);
    } catch {
      portraitInput.current?.click();
    }
  }

  function takePhoto() {
    const video = videoRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        stopCamera();
        void readFile(new File([blob], `portratt-${Date.now()}.jpg`, { type: "image/jpeg" })).then(
          setPortrait,
        );
      },
      "image/jpeg",
      0.9,
    );
  }

  async function addFiles(list: File[]) {
    const accepted: Attachment[] = [];
    for (const file of list) {
      if (files.length + accepted.length >= MAX_FILES) {
        toast.error(`Max ${MAX_FILES} bilagor.`);
        break;
      }
      if (file.size > MAX_BYTES) {
        toast.error(`${file.name} är för stor (max 8 MB).`);
        continue;
      }
      accepted.push(await readFile(file));
    }
    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
  }

  /** Kontrollerar fälten i webbläsaren så servern aldrig får en tom ansökan. */
  function validationErrors(): Partial<Record<
    "firstName" | "lastName" | "email" | "phone" | "coverLetter" | "consent",
    string
  >> {
    const M = {
      sv: {
        firstName: "Fyll i förnamn.",
        lastName: "Fyll i efternamn.",
        email: "Fyll i en giltig e-postadress.",
        phone: "Fyll i telefonnummer.",
        coverLetter: "Skriv några rader om dig själv (minst 30 tecken).",
        consent: "Du behöver godkänna integritetspolicyn.",
      },
      en: {
        firstName: "Enter your first name.",
        lastName: "Enter your last name.",
        email: "Enter a valid email address.",
        phone: "Enter a phone number.",
        coverLetter: "Write a few lines about yourself (at least 30 characters).",
        consent: "Please accept the privacy policy.",
      },
      es: {
        firstName: "Escribe tu nombre.",
        lastName: "Escribe tus apellidos.",
        email: "Escribe un correo electrónico válido.",
        phone: "Escribe un número de teléfono.",
        coverLetter: "Escribe unas líneas sobre ti (mínimo 30 caracteres).",
        consent: "Debes aceptar la política de privacidad.",
      },
    } as const;
    const m = M[locale] ?? M.sv;
    const e: Partial<Record<string, string>> = {};
    if (firstName.trim() === "") e["firstName"] = m.firstName;
    if (lastName.trim() === "") e["lastName"] = m.lastName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e["email"] = m.email;
    if (phone.trim().length < 4) e["phone"] = m.phone;
    if (coverLetter.trim().length < 30) e["coverLetter"] = m.coverLetter;
    if (!consent) e["consent"] = m.consent;
    return e;
  }

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        positionSlug: positionSlug || "spontan",
        positionLabel: positionOptions.find((p) => p.value === positionSlug)?.label ?? "",
        firstName,
        lastName,
        email,
        phone,
        city,
        country,
        earliestStart,
        extent,
        education,
        hasTeacherLicense: hasLicense,
        licenseDetails,
        licenseCountry,
        licenseSubjects,
        yearsExperience: years.trim() === "" ? null : Number(years),
        languages,
        languageLevels: languages.map((code) => ({
          code,
          level: languageLevels[code] ?? "fluent",
        })),
        driversLicense: drivers,
        driversClasses,
        driversCountry,
        coverLetter,
        references: refs,
        files: files.map((f) => ({ name: f.name, dataUrl: f.dataUrl })),
        portrait: portrait ? { name: portrait.name, dataUrl: portrait.dataUrl } : null,
        locale,
        sourcePath: typeof window === "undefined" ? "" : window.location.pathname,
        consent: consent as true,
        company,
        elapsedMs: Date.now() - startedAt.current,
        ...visitorFields(),
      };
      return submit({ data: payload });
    },
    onSuccess: () => {
      clearJobDraft();
      setDone(true);
      void goldConfetti();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },

    onError: (error) => {
      reportFormError((error as Error).message || "Ansökan kunde inte skickas.");
    },
  });

  if (done) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
        <h2 className="mt-4 text-2xl font-semibold">{t["doneTitle"]}</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm text-foreground/80">{t["doneBody"]}</p>
      </div>
    );
  }

  const errors = submitted ? validationErrors() : {};

  return (
    <form
      className="space-y-8 sm:space-y-10"
      onSubmit={(e) => {
        e.preventDefault();
        if (mutation.isPending) return;
        setSubmitted(true);
        const problems = validationErrors();
        const order = ["firstName", "lastName", "email", "phone", "coverLetter", "consent"] as const;
        const first = order.find((k) => problems[k]);
        if (first) {
          reportFormError(problems[first] as string, { anchorId: `job-${first}` });

          return;
        }
        mutation.mutate();
      }}
    >
      <div>
        <h2 className="text-xl font-semibold sm:text-2xl">{t["heading"]}</h2>
        <p className="mt-2 text-sm text-foreground/70">{t["intro"]}</p>
      </div>

      <section className="space-y-4">
        <SectionHeading icon={Briefcase} title={t["position"] ?? ""} />
        <SelectField
          label={t["position"] ?? ""}
          value={positionSlug}
          onChange={setPositionSlug}
          options={positionOptions}
          required
        />
      </section>

      <section className="space-y-4">
        <SectionHeading icon={User} title={t["about"] ?? ""} />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t["firstName"] ?? ""}
            anchorId="job-firstName"
            value={firstName}
            onChange={setFirstName}
            error={errors["firstName"]}
            required
            autoComplete="given-name"
          />
          <TextField
            label={t["lastName"] ?? ""}
            anchorId="job-lastName"
            value={lastName}
            onChange={setLastName}
            error={errors["lastName"]}
            required
            autoComplete="family-name"
          />
          <TextField
            label={t["email"] ?? ""}
            anchorId="job-email"
            type="email"
            inputMode="email"
            value={email}
            onChange={setEmail}
            error={errors["email"]}
            required
            autoComplete="email"
          />
          <PhoneField
            label={t["phone"] ?? ""}
            anchorId="job-phone"
            value={phone}
            onChange={setPhone}
            error={errors["phone"]}
            required
          />

          <TextField label={t["city"] ?? ""} value={city} onChange={setCity} autoComplete="address-level2" />
          <ComboboxField
            label={t["country"] ?? ""}
            value={country}
            onChange={setCountry}
            options={countryOptions()}
          />
          <TextField label={t["start"] ?? ""} type="date" value={earliestStart} onChange={setEarliestStart} />
          <SelectField
            label={t["extent"] ?? ""}
            value={extent}
            onChange={setExtent}
            options={JOB_EXTENTS.map((value) => ({ value, label: JOB_EXTENT_LABEL[value] }))}
          />
        </div>
      </section>

      <section className="space-y-4">
        <SectionHeading icon={GraduationCap} title={t["background"] ?? ""} />
        <TextareaField
          label={t["education"] ?? ""}
          value={education}
          onChange={setEducation}
          hint={t["educationHint"] ?? ""}
          rows={3}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label={t["years"] ?? ""} inputMode="numeric" value={years} onChange={(v) => setYears(v.replace(/\D/g, "").slice(0, 2))} />
        </div>

        <div className="space-y-3 rounded-md border border-border p-3 sm:p-4">
          <CheckboxField checked={hasLicense} onChange={setHasLicense}>
            {t["license"]}
          </CheckboxField>

          {hasLicense ? (
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <ComboboxField
                  label={t["licenseCountry"] ?? "Utfärdat i vilket land"}
                  value={licenseCountry}
                  onChange={setLicenseCountry}
                  options={LICENSE_COUNTRIES.map((c) => ({ value: c, label: c }))}
                />
                <TextField label={t["licenseDetails"] ?? ""} value={licenseDetails} onChange={setLicenseDetails} />
              </div>
              <ChipInput
                label={t["licenseSubjects"] ?? "Behörighet och ämnen"}
                values={licenseSubjects}
                onChange={setLicenseSubjects}
                suggestions={LICENSE_SUBJECTS}
                placeholder={t["licenseSubjectsHint"] ?? "T.ex. Grundskola åk 4–6"}
              />
            </div>
          ) : null}
        </div>

        <div className="space-y-3 rounded-md border border-border p-3 sm:p-4">
          <CheckboxField checked={drivers} onChange={setDrivers}>
            {t["drivers"]}
          </CheckboxField>

          {drivers ? (
            <div className="space-y-4">
              <ComboboxField
                label={t["driversCountry"] ?? "Körkortet är utfärdat i"}
                value={driversCountry}
                onChange={setDriversCountry}
                options={LICENSE_COUNTRIES.map((c) => ({ value: c, label: c }))}
              />
              <ChipInput
                label={t["driversClasses"] ?? "Körkortsklasser"}
                values={driversClasses}
                onChange={setDriversClasses}
                suggestions={DRIVER_CLASSES}
                placeholder="B"
              />
            </div>
          ) : null}
        </div>

        <div>
          <p className="mb-2 inline-flex items-center gap-2 text-sm font-medium">
            <LanguagesIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
            {t["languages"]}
          </p>
          <div className="space-y-2">
            {languages.map((code) => (
              <div
                key={code}
                className="grid grid-cols-[9rem_minmax(0,1fr)_auto] items-center gap-2 rounded-md border border-border bg-background p-2 sm:border-0 sm:bg-transparent sm:p-0"
              >
                <span className="flex min-w-0 items-center gap-2 text-sm">
                  <FlagIcon code={languageFlag(code)} size={20} />
                  <span className="min-w-0 truncate">{languageLabel(code)}</span>
                </span>
                <div className="min-w-0 order-2">
                  <SelectField
                    label=""
                    value={languageLevels[code] ?? "fluent"}
                    onChange={(v) => setLanguageLevels((prev) => ({ ...prev, [code]: v }))}
                    options={LANGUAGE_LEVELS.map((lvl) => ({
                      value: lvl,
                      label: LANGUAGE_LEVEL_LABEL[lvl],
                    }))}
                  />
                </div>
                <button
                  type="button"
                  aria-label={`${t["remove"]} ${languageLabel(code)}`}
                  className="order-3 grid h-9 w-9 shrink-0 cursor-pointer place-items-center rounded-md text-foreground/50 transition-colors hover:text-destructive"
                  onClick={() => setPendingLanguage(code)}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <SelectField
              label={t["addLanguage"] ?? "Lägg till språk"}
              value=""
              onChange={(v) => {
                if (!v || languages.includes(v)) return;
                setLanguages((prev) => [...prev, v]);
                setLanguageLevels((prev) => ({ ...prev, [v]: "fluent" }));
              }}
              options={LANGUAGE_OPTIONS.filter((o) => !languages.includes(o.code)).map((o) => ({
                value: o.code,
                label: o.label,
                flagCode: o.flag,
              }))}
            />
          </div>
        </div>
        <TextareaField
          label={t["letter"] ?? ""}
          anchorId="job-coverLetter"

          value={coverLetter}
          onChange={setCoverLetter}
          hint={t["letterHint"] ?? ""}
          error={errors["coverLetter"]}
          rows={8}
          required
        />
      </section>

      <section className="space-y-3">
        <SectionHeading
          icon={Paperclip}
          title={t["attachments"] ?? ""}
          hint={t["attachmentsHint"] ?? ""}
        />
        <div
          role="button"
          tabIndex={0}
          className="flex min-h-28 cursor-pointer items-center justify-center rounded-md border border-dashed border-border bg-sand/40 px-4 py-6 text-center text-sm text-foreground/70 transition-colors hover:border-primary/50"
          onClick={() => fileInput.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") fileInput.current?.click();
          }}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            void addFiles(Array.from(e.dataTransfer.files));
          }}
          onPaste={(e) => {
            const pasted = Array.from(e.clipboardData?.files ?? []);
            if (pasted.length) void addFiles(pasted);
          }}
        >
          <span className="inline-flex flex-col items-center gap-2 sm:flex-row">
            <Upload className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span className="text-balance">{t["dropzone"]}</span>
          </span>
        </div>
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.doc,.docx,image/*"
          onChange={(e) => {
            void addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
        {files.length > 0 ? (
          <ul className="space-y-2">
            {files.map((file, index) => (
              <li
                key={`${file.name}-${index}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border bg-background px-3 py-2 text-sm"
              >
                <span className="inline-flex min-w-0 items-center gap-2">
                  <FileText className="h-4 w-4 shrink-0 text-foreground/60" />
                  <span className="truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-foreground/50">{formatSize(file.size)}</span>
                </span>
                <button
                  type="button"
                  className="shrink-0 text-foreground/60 transition-colors hover:text-destructive"
                  aria-label={t["remove"] ?? "Ta bort"}
                  onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </section>

      <section className="space-y-3">
        <SectionHeading
          icon={ImageIcon}
          title={t["portrait"] ?? ""}
          hint={t["portraitHint"] ?? ""}
        />
        {camera ? (
          <div className="space-y-3">
            <video ref={videoRef} playsInline muted className="w-full max-w-sm rounded-md border border-border" />
            <div className="grid grid-cols-2 gap-2 sm:flex">
              <Button type="button" className="w-full sm:w-auto" onClick={takePhoto}>
                <Camera className="mr-2 h-4 w-4" /> {t["capture"]}
              </Button>
              <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={stopCamera}>
                <X className="mr-2 h-4 w-4" /> {t["stopCamera"]}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-3">
            {portrait ? (
              <img
                src={portrait.dataUrl}
                alt=""
                className="h-20 w-20 shrink-0 rounded-md border border-border object-cover"
              />
            ) : null}
            <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto">
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => portraitInput.current?.click()}
              >
                <Upload className="mr-2 h-4 w-4" /> {t["uploadPhoto"]}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => void startCamera()}
              >
                <Camera className="mr-2 h-4 w-4" /> {t["takePhoto"]}
              </Button>
              {portrait ? (
                <Button
                  type="button"
                  variant="ghost"
                  className="col-span-2 w-full sm:w-auto"
                  onClick={() => setPortrait(null)}
                >
                  <Trash2 className="mr-2 h-4 w-4" /> {t["remove"]}
                </Button>
              ) : null}
            </div>
          </div>
        )}
        <input
          ref={portraitInput}
          type="file"
          accept="image/*"
          capture="user"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void readFile(file).then(setPortrait);
            e.target.value = "";
          }}
        />
      </section>

      <section className="space-y-4">
        <SectionHeading icon={UserRoundCheck} title={t["references"] ?? ""} />
        {refs.map((ref, index) => (
          <div
            key={index}
            className="grid gap-4 rounded-md border border-border bg-background p-3 sm:grid-cols-2 sm:p-4"
          >
            <TextField
              label={t["refName"] ?? ""}
              value={ref.name}
              onChange={(v) => setRefs((prev) => prev.map((r, i) => (i === index ? { ...r, name: v } : r)))}
            />
            <TextField
              label={t["refRelation"] ?? ""}
              value={ref.relation}
              onChange={(v) => setRefs((prev) => prev.map((r, i) => (i === index ? { ...r, relation: v } : r)))}
            />
            <PhoneField
              label={t["refPhone"] ?? ""}
              value={ref.phone}
              onChange={(v) => setRefs((prev) => prev.map((r, i) => (i === index ? { ...r, phone: v } : r)))}
            />
            <TextField
              label={t["refEmail"] ?? ""}
              value={ref.email}
              onChange={(v) => setRefs((prev) => prev.map((r, i) => (i === index ? { ...r, email: v } : r)))}
            />
            <div className="sm:col-span-2">
              <TextareaField
                label={t["refNotes"] ?? ""}
                hint={t["refNotesHint"] ?? ""}
                rows={3}
                value={ref.notes}
                onChange={(v) =>
                  setRefs((prev) => prev.map((r, i) => (i === index ? { ...r, notes: v } : r)))
                }
              />
            </div>
          </div>
        ))}
        {refs.length < 4 ? (
          <Button
            type="button"
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => setRefs((prev) => [...prev, emptyRef()])}
          >
            <Plus className="mr-2 h-4 w-4" /> {t["addRef"]}
          </Button>
        ) : null}
      </section>

      <div className="hidden" aria-hidden="true">
        <input
          tabIndex={-1}
          autoComplete="off"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
        />
      </div>

      <div className="space-y-4 border-t border-border pt-6">
        <div id="job-consent">
          <CheckboxField checked={consent} onChange={setConsent} error={errors["consent"]}>
            <span>
              {t["consentPrefix"]}
              <button
                type="button"
                className="text-primary link-quiet underline-offset-2 hover:underline"
                onClick={() => setPrivacyOpen(true)}
              >
                {t["consentLink"]}
              </button>
              {t["consentSuffix"]}
            </span>
          </CheckboxField>
        </div>

        <Button
          type="submit"
          size="lg"
          className="w-full sm:w-auto"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t["sending"]}
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" /> {t["submit"]}
            </>
          )}
        </Button>
      </div>

      <PageModal
        open={privacyOpen}
        onOpenChange={setPrivacyOpen}
        href={PRIVACY_PATH[locale]}
        title={t["consentLink"] ?? ""}
        newTabLabel={
          locale === "en" ? "Open in new tab" : locale === "es" ? "Abrir en pestaña nueva" : "Öppna i ny flik"
        }
      />

      <AlertDialog open={pendingLanguage !== null} onOpenChange={(o) => (o ? null : setPendingLanguage(null))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {locale === "en"
                ? "Remove language?"
                : locale === "es"
                  ? "¿Quitar idioma?"
                  : "Ta bort språket?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingLanguage ? languageLabel(pendingLanguage) : ""}
              {locale === "en"
                ? " is removed from your application. You can add it again."
                : locale === "es"
                  ? " se quita de tu candidatura. Puedes añadirlo de nuevo."
                  : " tas bort från din ansökan. Du kan lägga till det igen."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {locale === "en" ? "Cancel" : locale === "es" ? "Cancelar" : "Avbryt"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const code = pendingLanguage;
                setPendingLanguage(null);
                if (!code) return;
                setLanguages((prev) => prev.filter((c) => c !== code));
                setLanguageLevels((prev) => {
                  const next = { ...prev };
                  delete next[code];
                  return next;
                });
              }}
            >
              {t["remove"]}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
